import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { manhwaList, categories } from '@/mocks/manhwa';
import type { Manhwa } from '@/types';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'update'>('update');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredManhwa = useMemo(() => {
    let result = [...manhwaList];

    if (currentCategory.slug !== 'all') {
      result = result.filter((m) => m.category === currentCategory.slug);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.author.toLowerCase().includes(q) ||
          m.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        result.sort((a, b) => parseInt(b.views) - parseInt(a.views));
        break;
      case 'update':
      default:
        result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }

    return result;
  }, [currentCategory, searchQuery, sortBy]);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {searchQuery ? `搜索：${searchQuery}` : currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共找到 {filteredManhwa.length} 部漫画
            </p>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {categories.slice(0, 9).map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    currentCategory.slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序：</span>
              {[
                { key: 'update' as const, label: '最近更新' },
                { key: 'rating' as const, label: '评分最高' },
                { key: 'views' as const, label: '人气最热' },
              ].map((option) => (
                <button
                  key={option.key}
                  onClick={() => setSortBy(option.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === option.key
                      ? 'bg-foreground-900 text-white'
                      : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {filteredManhwa.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {filteredManhwa.map((manhwa: Manhwa) => (
                <Link
                  key={manhwa.id}
                  to={`/article/${manhwa.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                    <img
                      src={manhwa.cover}
                      alt={manhwa.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={manhwa.title}
                    />
                    <div className="absolute top-2 left-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          manhwa.status === '连载中'
                            ? 'bg-primary-500 text-white'
                            : 'bg-foreground-600 text-white'
                        }`}
                      >
                        {manhwa.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {manhwa.title}
                  </h3>
                  <div className="flex items-center gap-1 mt-1">
                    <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                    <span className="text-xs text-foreground-600">{manhwa.rating}</span>
                    <span className="text-xs text-foreground-400 ml-2">{manhwa.chapters}话</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">没有找到相关漫画</p>
              <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600">
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} — 虚空韩漫网`,
            description: `虚空韩漫网${currentCategory.name}分类，浏览精选韩国漫画`,
            url: `https://www.xkmanhua.com/category/${currentCategory.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '虚空韩漫网',
              url: 'https://www.xkmanhua.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.xkmanhua.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: currentCategory.name,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}